// Shared cell/style helpers for the V-shape panels. Cell→scalar
// coercion itself is `cellScalar` in `#core/lib/expr/value.ts`
// (shared with the dashboard validation pass); these helpers cover
// the render-only concerns: placeholders, sorting, dash styles.

import type { ReactNode } from 'react';

import type { Stroke } from '#core/lib/expr/ops/dashboard/enums.ts';

/** Placeholder rendered for an empty (null/undefined) cell. SSOT —
 * shared by the table column renderers and the KPI metric tile. */
export const EMPTY = '—';

/** Comparator over the resolved scalar cell domain: nulls last,
 * numeric compare for numbers, locale compare for strings. SSOT —
 * used by the table sort and the Cartesian x-axis sort. */
export const compareScalar = (a: number | string | null, b: number | string | null): number => {
  if (a === null && b === null) {
    return 0;
  }
  if (a === null) {
    return 1;
  }
  if (b === null) {
    return -1;
  }
  if (typeof a === 'number' && typeof b === 'number') {
    return a - b;
  }
  return String(a).localeCompare(String(b));
};

/** Reads `record[accessor]`, collapsing null/undefined to {@link EMPTY}
 * once so per-format renderers only ever see a present scalar. SSOT for
 * the null-guard prelude repeated across the table column renderers. */
export const renderCell = (
  record: Record<string, number | string | null>,
  accessor: string,
  fn: (value: number | string) => ReactNode,
): ReactNode => {
  const value = record[accessor];
  if (value === null || value === undefined) {
    return EMPTY;
  }
  return fn(value);
};

/** recharts `strokeDasharray` for a dashboard stroke style. SSOT — used
 * by both series lines and reference markers. */
export const dashArray = (style: Stroke | undefined): string | undefined => {
  if (style === 'dashed') {
    return '6 3';
  }
  if (style === 'dotted') {
    return '2 2';
  }
  return undefined;
};

/** True when a stroke renders as a solid (unbroken) line — no dash pattern.
 * SSOT for "should this line show point dots?". */
export const isSolidStroke = (style: Stroke | undefined): boolean => dashArray(style) === undefined;
